const globalCache = new WeakMap();

/**
 * Memoization is a programming techique which allows reduce the function’s time
 * cost for space cost. That is, the functions which are memoized gains speed
 * for a higher use of memory space.
 * 
 * The memoization only can be used in pure functions, so the first point is known
 * that is a pure function.
 * 
 * How to use it?
 * Just add @Memoize on top of a method
 * 
 * About memoization:
 * https://dev.to/carlillo/understanding-javascripttypescript-qq-o7k
 * https://blog.angularindepth.com/how-to-improve-angular-performance-by-just-adding-just-8-characters-877bde708ddd
 * 
 * Code from:
 * https://gist.github.com/smnbbrv/dea470feba6ed20034f348b0610add66
 */
export function Memoize(): any {
	return (target: any, propertyName: string, descriptor: TypedPropertyDescriptor<any>) => {
		if (descriptor.value != null) {
			descriptor.value = getNewFunction(descriptor.value);
		} else if (descriptor.get != null) {
			descriptor.get = getNewFunction(descriptor.get);
		} else {
			throw new Error('Only put a Memoize decorator on a method or get accessor.');
		}
	};
}

class MixedMap {

	public map: Map<any, any> = new Map();
	public weakMap: WeakMap<any, any> = new WeakMap();

	public has(key: any): any {
		return this.getCorrespondingMap(key).has(key);
	}

	public get(key: any): any {
		return this.getCorrespondingMap(key).get(key);
	}

	public set(key: any, value: any): any {
		this.getCorrespondingMap(key).set(key, value);
		return this;
	}

	public clear(): void {
		this.map.clear();
		this.map = new Map();
		this.weakMap = new WeakMap();
	}

	private getCorrespondingMap(key: any): WeakMap<any, any> | Map<any, any> {
		return key instanceof Object ? this.weakMap : this.map;
	}

}

function getNewFunction(originalFunction: (...params: any[]) => void): any {
	return function (this: any, ...args: any[]): any {
		const thisCache = cache(globalCache, this, () => new WeakMap());
		const fnCache = cache(thisCache, originalFunction, () => new MixedMap());

		return cache(fnCache, args[0], () => originalFunction.apply(this, args));
	};
}

function cache<V>(wm: MixedMap | WeakMap<any, any>, key: any, createValue: () => V): any {
	let value: V;

	if (!wm.has(key)) {
		wm.set(key, value = createValue());
	} else {
		value = wm.get(key);
	}

	return value;
}
